<script setup>
import FadeTransition from "@/components/transitions/FadeTransition.vue";
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useQuery} from "@tanstack/vue-query";
import axios from "axios";

const emit = defineEmits(['progressPercentage', 'isCompleted', 'currentStep', 'failed'])

const props = defineProps({
  dataUrlPath: {
    type: String,
    required: true
  },
  jobPercentageWeights: {
    type: Array,
    required: false,
    default: () => [],
  },
  hidePercentage: {
    type: Boolean,
    required: false,
    default: false,
  }
});

const progress = ref(0);
const processingText = ref('Processing...');

watch(progress, newValue => {
  emit('progressPercentage', newValue);

  if (newValue === 100) {
    emit('isCompleted');
  }
});

const {data} = useQuery({
  queryKey: ['holding-step-job-progress', props.dataUrlPath],
  queryFn: async () => {
    const response = await axios.get(props.dataUrlPath);

    return response.data.data;
  },
  refetchInterval: 10000,
});

const jobsWithPercentages = computed(() => {
  if (!data.value) {
    return [];
  }

  const jobs = data.value.map((job, index) => {
    let percentage = props.jobPercentageWeights[index] ?? 0;

    return {
      ...job,
      percentage,
    };
  });

  const jobsWithoutPercent = jobs.filter(job => job.percentage === 0);
  const percentageUsed = jobs.reduce((accumulator, job) => accumulator + job.percentage, 0);
  const percentLeftPerJob = jobsWithoutPercent.length > 0 ?
    (100 - (Math.min(percentageUsed, 100))) / jobsWithoutPercent.length : 0;

  return jobs.map(job => {
    const maxPercentage = job.percentage || percentLeftPerJob;
    let percentage = 0;

    if (job.status === 'completed') {
      percentage = maxPercentage;
    }

    if (job.status === 'processing') {
      percentage = Math.floor(maxPercentage * 0.90);
    }

    return {
      ...job,
      percentage,
    };
  });
});

const targetProgress = computed(() => Math.max(10, Math.min(
  jobsWithPercentages.value.reduce((accumulator, job) => accumulator + job.percentage, 0),
  100,
)));

const currentStep = computed(() => {
  const calculatedStep = Math.floor(progress.value / (100 / totalJobs.value)) + 1;

  return Math.min(calculatedStep, totalJobs.value);
});

const totalJobs = computed(() => jobsWithPercentages.value.length);

const calculateIncrement = (remaining, isQuick) => {
  if (isQuick) {
    return (Math.random() * 3) + 1; // Quick mode: 1% to 4%
  }

  if (remaining <= 10) {
    return 1; // If within 10%, increment by 1% only
  }

  return (Math.random() * Math.min(2, remaining * 0.1)) + 1; // Normal mode: 1% to 2%, decreasing as we get closer to target value
};

const calculateDelay = (remaining, isQuick) => {
  if (isQuick) {
    return (Math.random() * 250) + 50; // Quick mode: 50ms to 300ms delay
  }

  if (remaining <= 10) {
    return (Math.random() * 14000) + 6000; // If within 10%, slow it down: 6s to 20s delay
  }

  return (Math.random() * 3000) + 1000; // Normal mode: delay by 1s to 4s
};

const randomIncrement = (targetValue, isQuick = false) => {
  if (progress.value >= targetValue) {
    return;
  }

  const remainingProgress = targetValue - progress.value;
  const increment = calculateIncrement(remainingProgress, isQuick);
  const delay = calculateDelay(remainingProgress, isQuick);

  // Ensure progress does not go beyond the target value
  progress.value = Math.floor(Math.min(progress.value + increment, targetValue));

  setTimeout(() => randomIncrement(targetValue, isQuick), delay);
};

const moveProgress = (targetValue, isQuick) => {
  randomIncrement(targetValue, isQuick);
};

watch(jobsWithPercentages, (newJobsWithPercentages) => {
  if (newJobsWithPercentages.filter(job => job.status === 'error').length > 0) {
    emit('failed');
  }
})

watch(targetProgress, (newTotalProgress) => {
  if (newTotalProgress === 100) {
    moveProgress(100, true);
    return;
  }

  moveProgress(newTotalProgress);
}, {
  immediate: true,
});

watch(currentStep, (newStep) => {
  emit('currentStep', newStep);
});

let interval;

onMounted(() => {
  interval = window.setInterval(() => {
    if (processingText.value === 'Processing...') {
      processingText.value = 'Processing.';
      return;
    }
    processingText.value = processingText.value+'.';

  }, 1000);
});

onUnmounted(() => {
  if (!interval) {
    return;
  }
  window.clearInterval(interval);
})
</script>

<template>
  <FadeTransition v-if="data">
    <div
      class="tw-flex tw-flex-col tw-w-full tw-gap-4"
    >
      <div class="tw-w-full tw-bg-grey-mid tw-rounded-full tw-h-2">
        <div
          class="tw-bg-blue tw-h-2 tw-rounded-full tw-transition-all tw-duration-900 tw-ease-in-out progress-bar"
          :style="{ width: progress + '%' }"
        />
      </div>

      <div class="tw-flex tw-justify-between tw-items-center">
        <span class="tw-text-blue tw-bg-blue-highlight tw-rounded-3xl tw-py-1 tw-px-3">
          Step {{ currentStep }} of {{ totalJobs }}
        </span>

        <span
          v-if="!hidePercentage"
          class="tw-text-grey-dark tw-text-xl tw-font-bold"
        >
          {{ progress }}% Completed
        </span>
        <span
          v-else
          class="tw-text-grey-dark tw-text-xl tw-font-bold text-left tw-min-w-[7.5rem] tw-flex-0"
        >
          {{ processingText }}
        </span>
      </div>
    </div>
  </FadeTransition>
</template>

<style scoped>
.progress-bar {
  background: linear-gradient(-45deg, rgb(3, 109, 186), rgb(0 148 255), rgb(75, 182, 255));
  background-size: 200% 200%;
  animation: gradient 5s ease infinite;
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
